/** API client: fetch wrapper with auth, refresh, retry. */
import { deleteImageCache, readCachedImage, writeCachedImage } from './imageCache';

/**
 * Outcome of a refresh attempt. The terminal/transient split is the reason a
 * network blip at the 30-minute access-token boundary no longer logs the user
 * out: only `auth_failed` clears the session; `transient` keeps it and surfaces
 * a retryable error.
 */
export type RefreshOutcome =
  | { status: 'ok'; accessToken: string }
  | { status: 'auth_failed' }   // 401 / revoked / missing token — terminal
  | { status: 'transient' };    // network / timeout / 5xx / malformed — keep session

export interface ClientConfig {
  baseUrl: string;
  getAccessToken: () => string | null;
  /**
   * Optional. Cloud web app omits this — its refresh token rides in an
   * HttpOnly cookie. The browser extension and self-hosted local-mode
   * clients still hand the token in here so it can go in the request body.
   */
  getRefreshToken?: () => string | null;
  /** Marker sent with body-mode refresh requests. Defaults to extension. */
  refreshClient?: 'extension' | 'local';
  /**
   * When set, refresh is delegated entirely to this handler and the built-in
   * cookie/body logic is skipped. The extension newtab uses this to route
   * refresh through the background service worker — the single realm that
   * holds and rotates the refresh token. Takes precedence over
   * getRefreshToken.
   */
  refreshHandler?: () => Promise<RefreshOutcome>;
  /**
   * Called after /api/auth/refresh succeeds. The server rotates the
   * refresh token (RFC 6749 §10.4); newRefreshToken is the replacement to
   * persist when the client owns the token. Cloud web app callers can
   * ignore this — the rotated token is set as a cookie by the server.
   */
  onTokenRefreshed: (accessToken: string, newRefreshToken?: string) => void;
  onAuthFailed: () => void;
  /**
   * Keep bookmark images on disk (CacheStorage) as well as in memory. The
   * extension newtab sets this: it has no service worker to run the CacheFirst
   * route markcat-app installs, so without it every newtab refetches its tiles.
   * The app leaves it off — its worker already owns the same cache and a second
   * writer would only double the bytes.
   */
  persistImages?: boolean;
}

let _config: ClientConfig | null = null;

export function configureClient(config: ClientConfig) {
  _config = config;
}

function getConfig(): ClientConfig {
  if (!_config) throw new Error('API client not configured. Call configureClient() first.');
  return _config;
}

/** Get the configured API base URL for building asset URLs. */
export function getBaseUrl(): string {
  return getConfig().baseUrl;
}

/** Live access-token getter backed by the client config. connectSSE takes this
 *  so its request-time injection reads the CURRENT token on every (re)open —
 *  a rotation must never tear the stream down just to swap a header. */
export function getAccessToken(): string | null {
  return getConfig().getAccessToken();
}

/** Pending refresh promise — prevents concurrent refresh calls. */
let _refreshPromise: Promise<RefreshOutcome> | null = null;

/** Serialize refresh across same-origin tabs. The web app's refresh token is a
 *  browser-shared HttpOnly cookie, so two tabs that hit an expired access token
 *  at once would both POST /refresh with the same token — the server rotates it
 *  on the first and the second presents a now-revoked token, getting logged out.
 *  The Web Lock makes the second tab wait until the first has rotated the
 *  cookie, so it refreshes with the current cookie instead. Falls back to direct
 *  execution where Web Locks are unavailable (Node tests, old browsers). */
async function withRefreshLock(fn: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  // The lock is held until the callback settles; capture the result out of it.
  // Default stays transient so a callback that never ran can't log anyone out.
  let outcome: RefreshOutcome = { status: 'transient' };
  await locks.request('markcat-refresh', async () => { outcome = await fn(); });
  return outcome;
}

/** Refresh the access token. Deduplicates concurrent calls within the tab
 *  (`_refreshPromise`) and across tabs (`withRefreshLock`).
 *
 * Three paths:
 * - Delegated (extension newtab): `refreshHandler` set. Refresh is owned by the
 *   background service worker; we just relay its outcome.
 * - Cookie mode (web app): no `getRefreshToken`. The HttpOnly `markcat_refresh`
 *   cookie carries the token; we send `credentials: 'include'` and an empty body.
 * - Body mode (local self-hosted): `getRefreshToken` returns the token; we send
 *   it in the body. Server tags the response `Deprecation: refresh-token-body`.
 *
 * A 401 is terminal (`auth_failed`); a network error / non-401 / malformed body
 * is `transient` so a blip does not log the user out.
 */
export async function refreshAccessToken(): Promise<RefreshOutcome> {
  if (_refreshPromise) return _refreshPromise;
  _refreshPromise = withRefreshLock(performRefresh);
  try {
    return await _refreshPromise;
  } finally {
    _refreshPromise = null;
  }
}

async function performRefresh(): Promise<RefreshOutcome> {
  const config = getConfig();

  if (config.refreshHandler) {
    const outcome = await config.refreshHandler();
    if (outcome.status === 'ok') config.onTokenRefreshed(outcome.accessToken);
    return outcome;
  }

  const refreshToken = config.getRefreshToken?.() ?? null;
  const useCookie = !config.getRefreshToken;
  if (!useCookie && !refreshToken) return { status: 'auth_failed' };

  let res: Response;
  try {
    // Body-mode (local self-hosted) tags the request so the server can keep
    // accepting body refreshes for these clients while rejecting body
    // refreshes from anywhere else. Cookie-mode clients (cloud web app) never
    // touch the body path. See routes_auth.refresh.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!useCookie) headers['X-Markcat-Client'] = config.refreshClient ?? 'extension';

    res = await fetch(`${config.baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: useCookie ? 'include' : 'same-origin',
      body: useCookie ? '{}' : JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    return { status: 'transient' };  // network / abort — session intact
  }

  if (res.status === 401) return { status: 'auth_failed' };
  if (!res.ok) return { status: 'transient' };

  let data: { access_token?: unknown; refresh_token?: unknown };
  try {
    data = await res.json();
  } catch {
    return { status: 'transient' };
  }
  if (typeof data.access_token !== 'string' || data.access_token.length === 0) {
    return { status: 'transient' };
  }
  const rotatedRefresh = typeof data.refresh_token === 'string' ? data.refresh_token : undefined;
  config.onTokenRefreshed(data.access_token, rotatedRefresh);
  return { status: 'ok', accessToken: data.access_token };
}

/** Trigger the configured terminal-logout callback. The SSE stream shares
 *  refreshAccessToken as the single refresh authority; on a revoked refresh
 *  token it drives the one clean logout through this, exactly like
 *  refreshOrThrow does for apiFetch. */
export function signalAuthFailed(): void {
  getConfig().onAuthFailed();
}

/** Resolve a 401 by refreshing once. Returns the new Bearer token to retry
 *  with, or throws — `auth_failed` clears the session (terminal), `transient`
 *  surfaces a retryable 503 without logging out. */
async function refreshOrThrow(): Promise<string> {
  const outcome = await refreshAccessToken();
  if (outcome.status === 'ok') return outcome.accessToken;
  if (outcome.status === 'auth_failed') {
    getConfig().onAuthFailed();
    throw new ApiError(401, 'Authentication failed');
  }
  throw new ApiError(503, 'Refresh temporarily failed');
}

/** Paths where the HttpOnly refresh cookie is in play. Limiting
 *  `credentials: 'include'` to these means non-auth routes don't broadcast
 *  cookie intent (no preflight surface, smaller blast radius if a server
 *  endpoint ever starts honoring an unexpected cookie). The cookie itself
 *  is `Path=/api/auth` so the browser wouldn't attach it elsewhere anyway,
 *  but explicit is better than implicit. */
function needsCredentials(path: string): boolean {
  return path.startsWith('/api/auth/');
}

const PUBLIC_AUTH_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
]);

function shouldAttemptRefresh(path: string): boolean {
  const pathname = path.split('?', 1)[0];
  return !PUBLIC_AUTH_PATHS.has(pathname);
}

/** The authenticated request behind every API call, up to a successful
 *  response: the configured origin, the current Bearer token, one refresh and
 *  retry on a 401 (logging out only on a terminal failure), and an `ApiError`
 *  for any other failure. Decoding the body is the caller's.
 *
 * `credentials: 'include'` is set only for `/api/auth/*` paths — the only
 * routes that interact with the refresh cookie. Other endpoints
 * (`/api/bookmarks/*`, `/api/events`, etc.) authenticate via Bearer and
 * don't need cookies attached. The caller's `signal` rides on both attempts.
 */
async function apiResponse(path: string, options: RequestInit): Promise<Response> {
  const config = getConfig();
  const url = `${config.baseUrl}${path}`;

  const headers = new Headers(options.headers);
  const token = config.getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  const credentials: RequestCredentials | undefined =
    needsCredentials(path) ? 'include' : undefined;

  let res = await fetch(url, { ...options, credentials, headers });

  if (res.status === 401 && shouldAttemptRefresh(path)) {
    headers.set('Authorization', `Bearer ${await refreshOrThrow()}`);
    res = await fetch(url, { ...options, credentials, headers });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new ApiError(res.status, messageFromDetail(body.detail, res.statusText), body.detail, retryAfterSeconds(res));
  }
  return res;
}

/** Authenticated JSON request; a 204 resolves to undefined. */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await apiResponse(path, options);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Authenticated request that returns a raw Blob (images, file downloads). */
export async function apiFetchBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  return (await apiResponse(path, options)).blob();
}

/** Parse a Retry-After header (delta-seconds form). Undefined when absent or
 *  unparseable. Drives client-side 429 pacing in importInBatches. */
function retryAfterSeconds(res: Response): number | undefined {
  const raw = res.headers.get('Retry-After');
  if (!raw) return undefined;
  const secs = Number(raw);
  return Number.isFinite(secs) && secs >= 0 ? secs : undefined;
}

/** FastAPI error bodies: {detail: string} or {detail: {error, message, ...}}. */
function messageFromDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'message' in detail) {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  return fallback;
}

/** Path-keyed cache of asset object URLs. The virtualizer unmounts off-screen
 *  rows, so without this every scroll-back re-downloads full screenshots. The
 *  Map stores the in-flight promise so concurrent requests for the same path
 *  de-dupe to a single fetch, and the resolved object URL is reused across
 *  mount/unmount cycles. The cache OWNS the lifecycle: it revokes the URL when
 *  the least-recently-used entry is evicted past the cap (a cache hit promotes
 *  the entry, so a still-visible asset is never the eviction target). Consumers
 *  MUST NOT revoke URLs returned from apiFetchObjectUrl. A rejected fetch is
 *  dropped so the next call retries. */
const ASSET_CACHE_MAX = 150;
const _assetCache = new Map<string, Promise<string>>();

function evictOldestAsset() {
  const oldest = _assetCache.keys().next().value;
  if (oldest === undefined) return;
  const url = _assetCache.get(oldest)!;
  _assetCache.delete(oldest);
  void url.then((u) => URL.revokeObjectURL(u)).catch(() => {});
}

/** Disk before network when persistImages is on, writing back what the network
 *  returns. A failed cache round-trip must not fail the image, so both sides
 *  fall through to the fetch. */
async function loadAssetBlob(path: string, options: RequestInit): Promise<Blob> {
  if (!getConfig().persistImages) return apiFetchBlob(path, options);

  const url = `${getConfig().baseUrl}${path}`;
  const stored = await readCachedImage(url).catch(() => null);
  if (stored) return stored;

  const blob = await apiFetchBlob(path, options);
  void writeCachedImage(url, blob).catch(() => {});
  return blob;
}

/** Authenticated fetch that returns a cached object URL for image/media
 *  rendering. The module cache owns the URL lifecycle — do NOT revoke. */
export function apiFetchObjectUrl(path: string, options: RequestInit = {}): Promise<string> {
  const cached = _assetCache.get(path);
  if (cached) {
    // Promote to most-recently-used so a still-visible asset is never the
    // oldest entry and thus never revoked out from under a live <img>.
    _assetCache.delete(path);
    _assetCache.set(path, cached);
    return cached;
  }

  const promise = loadAssetBlob(path, options)
    .then((blob) => URL.createObjectURL(blob))
    .catch((err) => {
      _assetCache.delete(path);  // drop failures so the next call retries
      throw err;
    });

  _assetCache.set(path, promise);
  if (_assetCache.size > ASSET_CACHE_MAX) evictOldestAsset();
  return promise;
}

/** Drop every image of the outgoing account — the in-memory object URLs (the
 *  cache owns their lifecycle, so a clear must revoke exactly as an eviction
 *  does) and the CacheStorage entries behind them. Logout is the caller: object
 *  URLs outlive the session otherwise, and cache entries carry no Vary, so the
 *  next account on this profile would be served these tiles. */
export function clearAssetCache(): void {
  for (const url of _assetCache.values()) {
    void url.then((u) => URL.revokeObjectURL(u)).catch(() => {});
  }
  _assetCache.clear();
  void deleteImageCache().catch(() => {});
}

export { IMAGE_CACHE_NAME } from './imageCache';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Raw `detail` body from the server (string for plain errors, object for
     *  structured errors such as quota_exceeded). Enables narrow-and-type checks
     *  like `isQuotaError()` without re-fetching or re-parsing. */
    public detail: unknown = undefined,
    /** Seconds from the response's Retry-After header, when present. */
    public retryAfter: number | undefined = undefined,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** User-friendly message for display in the UI. */
  get userMessage(): string {
    if (this.status === 401) return 'Session expired — please reconnect.';
    if (this.status === 403) return 'You don\u2019t have permission for this action.';
    if (this.status === 503) return 'Service is temporarily unavailable. Try again in a moment.';
    if (this.status >= 500) return 'Markcat’s server tripped. Give it a moment and retry.';
    return this.message;
  }
}

/** Structured body for HTTP 402 quota_exceeded responses. */
export interface QuotaError {
  error: 'quota_exceeded';
  metric: string;
  current: number;
  limit: number;
  plan: string;
  message: string;
}

/** Narrow an unknown error to a QuotaError if it matches the structured shape. */
export function isQuotaError(err: unknown): QuotaError | null {
  if (!(err instanceof ApiError) || err.status !== 402) return null;
  const d = err.detail;
  if (!d || typeof d !== 'object') return null;
  const obj = d as Record<string, unknown>;
  if (obj.error !== 'quota_exceeded') return null;
  if (typeof obj.metric !== 'string') return null;
  if (typeof obj.current !== 'number') return null;
  if (typeof obj.limit !== 'number') return null;
  if (typeof obj.plan !== 'string') return null;
  if (typeof obj.message !== 'string') return null;
  return obj as unknown as QuotaError;
}

/** Map network/fetch errors to user-friendly strings. */
export function friendlyErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.userMessage;
  if (err instanceof TypeError) return 'Can\u2019t reach your Markcat service. Check that it\u2019s running.';
  if (err instanceof DOMException && (err.name === 'TimeoutError' || err.name === 'AbortError')) return 'Request timed out. Check your connection.';
  if (err instanceof Error) return err.message;
  return 'An unexpected error occurred.';
}
